import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Award, BarChart2, BookOpen, ChevronDown, ChevronRight, Folder as FolderIcon, Globe, Home, Layers, Menu, Plus } from "lucide-react";
import { Folder } from "@/types/linguadeck";
import { AppState } from "@/types/linguadeck";
import NewDeckDropdown from "./NewDeckDropdown";
import type { DeckInitialType } from "./DeckModal";

interface SidebarProps {
  folders: Folder[];
  currentView: AppState["currentView"];
  onNavTo: (view: AppState["currentView"]) => void;
  onNewDeck: (type: DeckInitialType) => void;
  onCreateFolder?: () => void;
}

const navItems = [
  { id: "home" as const, path: "/", icon: <Home size={18} strokeWidth={2} />, labelKey: "sidebar.home" },
  { id: "library" as const, path: "/library", icon: <Layers size={18} strokeWidth={2} />, labelKey: "sidebar.mySets" },
  { id: "globalLibrary" as const, path: "/global-library", icon: <Globe size={18} strokeWidth={2} />, labelKey: "sidebar.globalLibrary" },
  { id: "gamification" as const, path: "/gamification", icon: <Award size={18} strokeWidth={2} />, labelKey: "header.achievements" },
  { id: "dashboard" as const, path: "/dashboard", icon: <BarChart2 size={18} strokeWidth={2} />, labelKey: "statistics.pageTitle" },
];

function NavButton({
  active,
  onClick,
  icon,
  label,
  collapsed,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: collapsed ? 0 : 10,
        padding: collapsed ? "9px" : "9px 12px",
        borderRadius: "var(--radius-sm)",
        cursor: "pointer",
        transition: "all .2s",
        marginBottom: 2,
        width: "100%",
        border: "none",
        background: active ? "rgba(109,90,255,.15)" : "transparent",
        borderLeft: active ? "3px solid hsl(var(--primary))" : "3px solid transparent",
        color: "hsl(var(--foreground))",
        fontFamily: "DM Sans, sans-serif",
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        textAlign: "left",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{icon}</span>
      {!collapsed && <span>{label}</span>}
    </button>
  );
}

function SectionLabel({ children, collapsed }: { children: React.ReactNode; collapsed?: boolean }) {
  if (collapsed) return null;
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.1em",
        color: "hsl(var(--muted-foreground))",
        padding: "6px 10px",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

export default function Sidebar({
  folders,
  currentView,
  onNavTo,
  onNewDeck,
  onCreateFolder,
}: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 768);
  const [isFoldersOpen, setIsFoldersOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarFoldersOpen");
    return saved !== "false";
  });

  useEffect(() => {
    localStorage.setItem("sidebarFoldersOpen", String(isFoldersOpen));
  }, [isFoldersOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setIsCollapsed(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <aside
      className="flex h-full max-h-full flex-col flex-shrink-0 m-0 transition-all duration-300 overflow-hidden"
      style={{
        width: isCollapsed ? 80 : 260,
        background: "hsl(var(--bg2))",
        boxSizing: "border-box",
      }}
    >
      {/* Logo + Hamburger — when collapsed: Menu only; when expanded: full logo */}
      <div
        className="flex-shrink-0"
        style={{
          padding: isCollapsed ? "12px 8px" : "16px 20px",
          borderBottom: "1px solid hsl(var(--border))",
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={() => setIsCollapsed((c) => !c)}
          title={isCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors shrink-0"
          style={{ border: "none", background: "transparent", cursor: "pointer", color: "hsl(var(--foreground))" }}
        >
          <Menu size={20} strokeWidth={2} />
        </button>
        {!isCollapsed && (
          <div className="flex flex-col items-center min-w-0 gap-1" style={{ background: "transparent" }}>
            <div className="flex items-center justify-center gap-2">
              <BookOpen className="w-8 h-8 text-primary" style={{ color: "hsl(var(--primary))" }} />
              <span className="font-bold text-lg" style={{ color: "hsl(var(--foreground))" }}>StudyDo</span>
            </div>
            <div className="text-sm font-medium text-muted-foreground">
              by Alina Guseva
            </div>
          </div>
        )}
      </div>

      {/* Nav — fixed at top */}
      <div
        className="flex-shrink-0"
        style={{
          padding: isCollapsed ? "8px" : "12px",
          borderBottom: "1px solid hsl(var(--border))",
          display: "flex",
          flexDirection: "column",
          alignItems: isCollapsed ? "center" : "stretch",
        }}
      >
        <SectionLabel collapsed={isCollapsed}>{t("sidebar.menu")}</SectionLabel>
        {navItems.map((item) => {
          const isPathNav = "path" in item;
          const active = isPathNav
            ? (item.path === "/global-library" ? location.pathname.startsWith("/global-library") : location.pathname === item.path)
            : currentView === item.id;
          const handleClick = () => {
            if (item.id === "home") {
              onNavTo("home");
              navigate("/");
            } else if (item.id === "gamification") {
              onNavTo("gamification");
              navigate("/gamification");
            } else if (item.id === "library") {
              onNavTo("library");
              navigate("/library");
            } else if (item.id === "dashboard") {
              onNavTo("dashboard");
              navigate("/dashboard");
            } else if (item.id === "globalLibrary") {
              onNavTo("library");
              navigate("/global-library");
            } else if (isPathNav) {
              navigate(item.path);
            } else {
              onNavTo(item.id);
            }
          };
          return (
            <NavButton
              key={item.labelKey}
              active={active}
              onClick={handleClick}
              icon={item.icon}
              label={t(item.labelKey)}
              collapsed={isCollapsed}
            />
          );
        })}
      </div>

      {/* ── Scrollable content (decks, words, cards, tests) ──────────────────── */}
      <div
        className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden"
        style={{ padding: isCollapsed ? "8px" : "12px", borderBottom: "1px solid hsl(var(--border))" }}
      >
        {isCollapsed ? (
          /* Collapsed: Folder icon linking to first folder or /library */
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <button
              onClick={() => {
                const firstFolder = (folders ?? [])[0];
                if (firstFolder) navigate(`/folder/${firstFolder.id}`);
                else navigate("/library");
              }}
              title={t("sidebar.folders")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: "var(--radius-sm)",
                border: "none",
                background: location.pathname.startsWith("/folder/") ? "rgba(109,90,255,.15)" : "transparent",
                cursor: "pointer",
                color: "hsl(var(--foreground))",
              }}
              onMouseEnter={(e) => {
                if (!location.pathname.startsWith("/folder/")) e.currentTarget.style.background = "hsl(var(--bg3))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = location.pathname.startsWith("/folder/") ? "rgba(109,90,255,.15)" : "transparent";
              }}
            >
              <FolderIcon size={22} strokeWidth={2} />
            </button>
          </div>
        ) : (
        <>
        {/* ── Folders section (collapsible) ────────────────────────────────────── */}
        {!isCollapsed && (
        <div style={{ marginTop: 16, marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => setIsFoldersOpen((open) => !open)}
            className="flex items-center justify-between w-full rounded-lg transition-colors hover:bg-[hsl(var(--bg3))]"
            style={{
              padding: "6px 10px",
              marginBottom: 6,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontFamily: "DM Sans, sans-serif",
            }}
            aria-expanded={isFoldersOpen}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                color: "hsl(var(--muted-foreground))",
                textTransform: "uppercase",
              }}
            >
              {t("sidebar.folders")}
            </span>
            {isFoldersOpen ? (
              <ChevronDown size={14} style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
            ) : (
              <ChevronRight size={14} style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
            )}
          </button>
          <div
            className="overflow-hidden ease-out"
            style={{
              display: "grid",
              gridTemplateRows: isFoldersOpen ? "1fr" : "0fr",
              transition: "grid-template-rows 200ms ease-out",
            }}
          >
            <div className="min-h-0 overflow-hidden">
              {(folders ?? []).length === 0 ? (
                <div
                  style={{
                    padding: "12px 10px",
                    fontSize: 12,
                    color: "hsl(var(--muted-foreground))",
                  }}
                >
                  {t("folder.noFolders")}
                </div>
              ) : (
                (folders ?? []).map((folder) => (
                  <div
                    key={folder.id}
                    onClick={() => navigate(`/folder/${folder.id}`)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: "var(--radius-sm)",
                      cursor: "pointer",
                      transition: "all .2s",
                      marginBottom: 2,
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "hsl(var(--bg3))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <FolderIcon size={14} style={{ color: "hsl(var(--muted-foreground))" }} />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "hsl(var(--foreground))",
                      }}
                    >
                      {folder.title}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
          {/* "+ New folder" — always visible below the collapsible list */}
          {onCreateFolder && (
            <button
              type="button"
              onClick={onCreateFolder}
              className="flex items-center gap-3 w-full px-3 py-2 mt-3 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-[hsl(var(--bg3))] hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))]/80 rounded-xl transition-colors"
              style={{
                border: "none",
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              <Plus size={18} className="shrink-0" />
              {t("folder.newFolder")}
            </button>
          )}
        </div>
        )}
        </>
        )}

      </div>

      {/* Bottom — pinned outside scroll area. Hidden on mobile; FAB shown in Index instead */}
      <div
        className="hidden md:flex flex-shrink-0 border-t"
        style={{
          padding: isCollapsed ? 12 : 16,
          borderTopColor: "hsl(var(--border))",
          background: "hsl(var(--bg2))",
          justifyContent: isCollapsed ? "center" : "flex-start",
        }}
      >
        <NewDeckDropdown onSelect={onNewDeck} onCreateFolder={onCreateFolder} variant={isCollapsed ? "icon" : "full"} openUpwards />
      </div>
    </aside>
  );
}
